import { useTranslation } from "react-i18next";
import { getCurrentPeriod, getPerformanceYearShort, getPrevYearShort } from "../../../../common/util/period";
import defaultContent from "../../../../content/downlineProfile";
import {
  IconTrendArrow,
  TrendArrowDirection,
  TrendArrowColor,
} from "../../../../common/icons/IconTrendArrow/IconTrendArrow";
import { storedLocale } from "../../../Authentication/util";
import NoDataValue from "../../../../common/components/NoDataValue/NoDataValue";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import LeafPinLevelName from "../../../../common/leaf/LeafPinLevelName/LeafPinLevelName";
import { useAppSelector } from "../../../../store";
import { pinDate } from "../../../../common/util/formatDate";

interface KeyInformationAwardRowProps {
  label?: string;
  pin?: number;
  level?: string;
  date?: number;
  up?: string;
  name?: string;
  isValid?: boolean;
}

export const DATE_CONVERSION: Record<string, { direction: TrendArrowDirection; color: TrendArrowColor }> = {
  HIGHER: { direction: "up", color: "green" },
  LOWER: { direction: "down", color: "red" },
};

const KeyInformationAwardsRow = ({ label, pin, level, date, up, name, isValid }: KeyInformationAwardRowProps) => {
  const { t } = useTranslation(["downlineProfile"]);
  const { configuration } = useAppSelector((state) => state.boot);

  const toPY = (date: string) => {
    if (!date) return "";
    return `${getPerformanceYearShort(date.replace("-", ""))}`;
  };

  const isEmpty = name === "--";

  const newDate = date
    ? toPY(`${date}`)
    : label === "previousAward"
      ? `${getPrevYearShort(getCurrentPeriod())}`
      : label === "trackingAward"
        ? toPY(getCurrentPeriod())
        : "";

  const rowRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLParagraphElement>(null);

  const measure = useCallback(() => {
    const wrapper = wrapperRef.current;
    const p = textRef.current;
    const textNode = p?.firstChild;
    if (!wrapper || !p || !textNode) return;

    const text = textNode.textContent?.trim() ?? "";
    const isShort = text.length <= 5;
    p.style.whiteSpace = isShort ? "nowrap" : "";
    wrapper.style.flexShrink = isShort ? "0" : "";

    wrapper.style.setProperty("--measured-text-width", "auto");

    requestAnimationFrame(() => {
      const range = document.createRange();
      range.selectNodeContents(textNode);
      const rects = Array.from(range.getClientRects());
      const widest = Math.max(...rects.map((r) => r.width));
      if (!Number.isFinite(widest) || widest <= 0) return;

      wrapper.style.setProperty("--measured-text-width", `${Math.ceil(widest)}px`);
    });
  }, []);

  useLayoutEffect(() => {
    measure();
  }, [measure, level]);

  useEffect(() => {
    const row = rowRef.current;
    if (!row) return;

    let timeoutId: ReturnType<typeof setTimeout>;
    const observer = new ResizeObserver(() => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(measure, 100);
    });
    observer.observe(row);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [measure]);
  if (!isValid) {
    return null;
  }

  return (
    <>
      <div className="awards-row" ref={rowRef}>
        <p>
          {label !== "highestAward"
            ? `${
                label
                  ? t(
                      `keyInformation.awardLabels.${label}`,
                      defaultContent.keyInformation.awardLabels[
                        label as keyof typeof defaultContent.keyInformation.awardLabels
                      ] as string,
                      { date: newDate },
                    )
                  : ""
              }`
            : t("keyInformation.awardLabels.highestAward", defaultContent.keyInformation.awardLabels.highestAward)}
        </p>

        <span>
          {pin === 0 || !pin || isEmpty ? (
            <>
              <NoDataValue label="No Data Available" />
            </>
          ) : (
            <div className="awards-row__level">
              <div className="awards-row__level__pinRow">
                <div className="awards-row__level__text" ref={wrapperRef}>
                  <p ref={textRef}>
                    <LeafPinLevelName pinLevelCode={pin} />
                  </p>
                </div>
                {up && up !== "EQUAL" && (
                  <div
                    className={`arrow-badge ${up === "HIGHER" && `arrow-badge--green`} ${up === "LOWER" && `arrow-badge--red`}`}
                  >
                    <IconTrendArrow {...DATE_CONVERSION[up as keyof typeof DATE_CONVERSION]} />
                  </div>
                )}
              </div>
              {label === "highestAward" && date && (
                <div>
                  <p>{pinDate(`${date}`, storedLocale(), configuration?.profile?.format?.date ?? "long")}</p>
                </div>
              )}
            </div>
          )}
        </span>
      </div>
    </>
  );
};

export default KeyInformationAwardsRow;
